/** SRI catalogues used by the app. Codes are the wire values. */

export const COD_DOC = ['01', '03', '04', '05', '06', '07'] as const;
export type CodDoc = (typeof COD_DOC)[number];

export const COD_DOC_LABEL: Record<CodDoc, string> = {
  '01': 'Factura',
  '03': 'Liquidación de compra',
  '04': 'Nota de crédito',
  '05': 'Nota de débito',
  '06': 'Guía de remisión',
  '07': 'Comprobante de retención',
};

/** Order shown in the "tipo de comprobante" picker. */
export const COD_DOC_PICKER_ORDER: CodDoc[] = ['01', '04', '05', '07', '06', '03'];

/** Voucher types that carry `totales` + `pagos` in the emit body. */
export function hasPagos(codDoc: CodDoc): boolean {
  return codDoc !== '06' && codDoc !== '07';
}
/** Voucher types that reference a "documento sustento/modificado". */
export function hasDocModificado(codDoc: CodDoc): boolean {
  return codDoc === '04' || codDoc === '05' || codDoc === '07';
}

/** Parse `voucherType|tipoComprobante|codDoc|tipo` (name or code) into a CodDoc. */
export function parseCodDoc(v: unknown): CodDoc {
  const s = String(v ?? '').trim();
  if ((COD_DOC as readonly string[]).includes(s)) return s as CodDoc;
  const lower = s.toLowerCase().replace(/[\s_-]/g, '');
  if (lower.includes('liquidacion')) return '03';
  if (lower.includes('credito')) return '04';
  if (lower.includes('debito')) return '05';
  if (lower.includes('guia') || lower.includes('remision')) return '06';
  if (lower.includes('retencion')) return '07';
  return '01';
}

/** codDocModificado options (NC/ND/retención sustento). */
export const COD_DOC_MODIFICADO = [
  { code: '01', label: 'Factura (01)' },
  { code: '03', label: 'Liquidación de compra (03)' },
  { code: '41', label: 'Comprobante de retención (41)' },
] as const;

export const TIPO_IDENTIFICACION = ['04', '05', '06', '07', '08'] as const;
export type TipoIdentificacion = (typeof TIPO_IDENTIFICACION)[number];
export const CONSUMIDOR_FINAL_ID = '9999999999999';

/** Auto-detect from the typed identificación (mobile behaviour). */
export function detectTipoIdentificacion(id: string): TipoIdentificacion | null {
  const s = id.trim();
  if (s === CONSUMIDOR_FINAL_ID) return '07';
  if (/^\d{13}$/.test(s)) return '04';
  if (/^\d{10}$/.test(s)) return '05';
  return null;
}

export const IVA_OPCIONES = ['IVA_0', 'IVA_5', 'IVA_12', 'IVA_13', 'IVA_15', 'IVA_NO_OBJETO', 'IVA_EXENTO'] as const;
export type IvaOpcion = (typeof IVA_OPCIONES)[number];

export const IVA_RATE: Record<IvaOpcion, number> = {
  IVA_0: 0,
  IVA_5: 0.05,
  IVA_12: 0.12,
  IVA_13: 0.13,
  IVA_15: 0.15,
  IVA_NO_OBJETO: 0,
  IVA_EXENTO: 0,
};

export function ivaRateFor(opcion: string | null | undefined, fallbackRate?: number): number {
  if (opcion && opcion in IVA_RATE) return IVA_RATE[opcion as IvaOpcion];
  return fallbackRate ?? 0;
}

export function ivaPercentLabel(opcion: string): string {
  const m = /^IVA_(\d+)$/.exec(opcion);
  if (m) return `${m[1]}%`;
  if (opcion === 'IVA_NO_OBJETO') return 'No objeto';
  if (opcion === 'IVA_EXENTO') return 'Exento';
  return opcion;
}

/** SRI tabla 24. Only the first four are exposed as quick picks in the UI. */
export const FORMA_PAGO = ['01', '15', '16', '17', '18', '19', '20', '21'] as const;
export type FormaPago = (typeof FORMA_PAGO)[number];
export const FORMA_PAGO_QUICK: FormaPago[] = ['01', '20', '16', '19'];

export const FORMA_PAGO_SRI_LABEL: Record<FormaPago, string> = {
  '01': 'SIN UTILIZACION DEL SISTEMA FINANCIERO',
  '15': 'COMPENSACIÓN DE DEUDAS',
  '16': 'TARJETA DE DÉBITO',
  '17': 'DINERO ELECTRÓNICO',
  '18': 'TARJETA PREPAGO',
  '19': 'TARJETA DE CRÉDITO',
  '20': 'OTROS CON UTILIZACION DEL SISTEMA FINANCIERO',
  '21': 'ENDOSO DE TÍTULOS',
};

export function formaPagoSriLabel(code: string): string {
  return (FORMA_PAGO_SRI_LABEL as Record<string, string>)[code] ?? 'OTROS';
}

export type Ambiente = '1' | '2';

export function parseAmbiente(v: unknown, claveAcceso?: string | null): Ambiente | null {
  const s = String(v ?? '').trim().toUpperCase();
  if (s === '1' || s === 'PRUEBAS') return '1';
  if (s === '2' || s === 'PRODUCCION' || s === 'PRODUCCIÓN') return '2';
  // Fallback: digit 24 (index 23) of the 49-digit clave de acceso.
  if (claveAcceso && claveAcceso.length === 49) {
    const d = claveAcceso[23];
    if (d === '1' || d === '2') return d;
  }
  return null;
}
